
import React from 'react';
import { Seat, SeatType, LogicalCoords } from '../types';

interface SeatMapProps {
  seats: Seat[];
  rows: number;
  cols: number;
  logicalMap?: Map<string, LogicalCoords>; // Map of seat ID to logical numbers
  onSeatClick?: (seat: Seat) => void;
  onSeatDoubleClick?: (seat: Seat) => void;
  readOnly?: boolean;
  highlightSeatId?: string | null; // For the yellow highlight effect
  cellSize?: number; // Optional custom cell size for zooming/large displays
}

const SeatTypeName: Record<SeatType, string> = {
  [SeatType.STANDARD]: '普通座位',
  [SeatType.AISLE]: '过道',
  [SeatType.STAGE]: '主席台',
  [SeatType.EMPTY]: '空位',
  [SeatType.UNAVAILABLE]: '不可用',
};

export const SeatMap: React.FC<SeatMapProps> = ({ 
  seats, 
  rows, 
  cols, 
  logicalMap, 
  onSeatClick, 
  onSeatDoubleClick, 
  readOnly = false,
  highlightSeatId,
  cellSize = 40 // Default to 40px
}) => {
  // Group seats by row for rendering
  const grid: Seat[][] = Array.from({ length: rows }, () => Array(cols).fill(null));
  seats.forEach(s => {
    if (s.row >= 0 && s.row < rows && s.col < cols) {
      grid[s.row][s.col] = s;
    }
  });

  // Calculate Row Labels based on logical map
  const rowLabelMap = new Map<number, string>();
  if (logicalMap) {
      for (let r = 0; r < rows; r++) {
          const rowSeats = seats.filter(s => s.row === r);
          const standardSeat = rowSeats.find(s => s.type === SeatType.STANDARD && logicalMap.get(s.id)?.isValid);
          if (standardSeat) {
              const logical = logicalMap.get(standardSeat.id);
              if (logical) {
                  rowLabelMap.set(r, `第${logical.rowLabel}排`);
              }
          }
      }
  }

  // Helper to determine font size
  const getDynamicFontSize = (text: string, type: SeatType) => {
      const len = text ? text.length : 0;
      if (len === 0) return cellSize * 0.35;

      // Occupy ~90% of the cell width
      // For standard seats (Names)
      if (type === SeatType.STANDARD) {
          // Base size is calculated to fit 3 characters comfortably within 90% of width.
          // 3 chars * 0.30 = 0.90
          const baseSize = cellSize * 0.30;

          if (len <= 3) {
              // 2 or 3 characters (including the added space) use the standard base size
              return baseSize;
          } else {
              // More than 3 characters: shrink to fit the same max width
              // width = len * size = cellSize * 0.90
              // size = (cellSize * 0.90) / len
              return (cellSize * 0.90) / len;
          }
      }
      
      // For Stage/Aisle
      return cellSize * 0.35; 
  };

  // Base font size for static labels (Side row numbers)
  const sideLabelSize = cellSize * 0.35; 

  const getSeatClasses = (seat: Seat, rIndex: number, cIndex: number, isHighlighted: boolean) => {
    if (!seat) return 'bg-transparent';
    
    // Base classes
    let classes = 'transition-all select-none flex flex-col items-center justify-center ';

    // Specific Styles
    if (seat.type === SeatType.EMPTY) {
        classes += 'bg-transparent border border-dashed border-gray-200 opacity-30 m-0.5 rounded-sm print:opacity-0';
    } else if (seat.type === SeatType.UNAVAILABLE) {
        classes += 'bg-red-50 border border-red-200 text-red-300 cursor-not-allowed m-0.5 rounded-sm print:hidden';
    } else if (seat.type === SeatType.AISLE) {
        // Aisle - Slightly darker gray as requested
        classes += 'bg-gray-300 text-gray-600 font-bold m-0 z-0 print:bg-white print:text-black';
    } else if (seat.type === SeatType.STAGE) {
        // Stage - Bold font
        // PRINT: bg-white, text-black, border-black
        classes += 'bg-amber-100 text-amber-800 font-bold z-10 hover:bg-amber-200 print:bg-white print:text-black ';
        
        const top = rIndex > 0 ? grid[rIndex - 1][cIndex] : null;
        const bottom = rIndex < rows - 1 ? grid[rIndex + 1][cIndex] : null;
        const left = cIndex > 0 ? grid[rIndex][cIndex - 1] : null;
        const right = cIndex < cols - 1 ? grid[rIndex][cIndex + 1] : null;

        const isTopStage = top?.type === SeatType.STAGE;
        const isBottomStage = bottom?.type === SeatType.STAGE;
        const isLeftStage = left?.type === SeatType.STAGE;
        const isRightStage = right?.type === SeatType.STAGE;

        // Visual Borders:
        // Regular mode: border-amber-400, border-2
        // Print mode: border-black, border-2 ONLY on the outside. 
        // Logic: if neighbor is stage, NO border. If neighbor is NOT stage, YES border.

        classes += isTopStage ? 'border-t-0 ' : 'border-t-2 border-amber-400 print:border-black ';
        classes += isBottomStage ? 'border-b-0 ' : 'border-b-2 border-amber-400 print:border-black ';
        classes += isLeftStage ? 'border-l-0 ' : 'border-l-2 border-amber-400 print:border-black ';
        classes += isRightStage ? 'border-r-0 ' : 'border-r-2 border-amber-400 print:border-black ';

        if (!isTopStage && !isLeftStage) classes += 'rounded-tl-md ';
        if (!isTopStage && !isRightStage) classes += 'rounded-tr-md ';
        if (!isBottomStage && !isLeftStage) classes += 'rounded-bl-md ';
        if (!isBottomStage && !isRightStage) classes += 'rounded-br-md ';

    } else {
        // Standard
        if (isHighlighted) {
            // DRIVER.JS Style Highlight: Bright Yellow
            classes += 'bg-yellow-400 text-black border-2 border-yellow-600 shadow-lg scale-110 z-50 rounded-md font-bold animate-pulse print:bg-white print:border-black print:text-black print:shadow-none print:animate-none print:scale-100 ';
        } else if (seat.attendeeName) {
            // Occupied - Light Blue as requested
            // PRINT: bg-white, border-black (no background color)
            classes += 'bg-sky-200 text-sky-900 border border-sky-300 shadow-sm hover:bg-sky-300 m-0.5 rounded-md z-10 print:bg-white print:text-black print:border-2 print:border-black print:shadow-none print:font-bold ';
        } else {
            // Empty Standard
            classes += 'bg-white border border-gray-200 hover:bg-gray-50 m-0.5 rounded-md z-10 print:border-gray-300 print:border-dashed ';
        }
    }
    
    return classes;
  };

  const renderSeat = (seat: Seat | null, rIndex: number, cIndex: number) => {
      const key = seat ? seat.id : `empty-${rIndex}-${cIndex}`;
      const logical = seat && logicalMap ? logicalMap.get(seat.id) : null;
      const isHighlighted = seat?.id === highlightSeatId;

      let mainText = '';
      let fontSize = cellSize * 0.35;
      let fontWeight = 'normal';
      let isTwoCharChinese = false;

      if (seat) {
        if (seat.attendeeName) {
            mainText = seat.attendeeName;
            
            // Detect 2-character Chinese names for special justification
            if (seat.type === SeatType.STANDARD && mainText.length === 2 && /[\u4e00-\u9fa5]/.test(mainText)) {
                isTwoCharChinese = true;
            }

            fontWeight = '500'; 
            fontSize = getDynamicFontSize(mainText, SeatType.STANDARD);
        } else if (seat.type === SeatType.AISLE) {
             mainText = seat.label || '';
             fontWeight = 'bold'; // User requested bold
             fontSize = getDynamicFontSize(mainText, SeatType.AISLE);
        } else if (seat.type === SeatType.STAGE) {
             mainText = seat.label || '主席台'; 
             if (seat.label) {
                 mainText = seat.label;
             } else {
                 mainText = ''; 
             }
             fontWeight = 'bold'; // User requested bold
             fontSize = getDynamicFontSize(mainText, SeatType.STAGE);
        } else if (seat.type === SeatType.STANDARD && logical?.isValid) {
             if (!readOnly) {
                 mainText = `${logical.rowLabel}-${logical.colLabel}`;
                 fontSize = cellSize * 0.25; // Smaller for coords
             } else {
                 mainText = '';
             }
        }
      }

      return (
        <div
            key={key}
            id={seat ? seat.id : undefined}
            onClick={() => seat && !readOnly && onSeatClick && onSeatClick(seat)}
            onDoubleClick={() => seat && !readOnly && onSeatDoubleClick && onSeatDoubleClick(seat)}
            className={`
              relative h-full w-full
              ${getSeatClasses(seat!, rIndex, cIndex, isHighlighted)}
              ${!readOnly && seat ? 'cursor-pointer hover:brightness-95' : ''}
            `}
            title={seat ? `${SeatTypeName[seat.type]} ${logical?.isValid ? `(${logical.rowLabel}排${logical.colLabel}列)` : ''}` : ''}
        >
            {seat && (
                <div className="flex flex-col items-center justify-center w-full h-full p-0 overflow-hidden">
                    <div 
                        className={`leading-none whitespace-nowrap ${seat.type === SeatType.STANDARD && !seat.attendeeName ? 'text-gray-300' : ''} ${isTwoCharChinese ? 'flex justify-between w-[90%] mx-auto' : 'text-center w-full'}`}
                        style={{ fontSize: `${fontSize}px`, fontWeight }}
                    >
                        {isTwoCharChinese ? (
                            <>
                                <span>{mainText[0]}</span>
                                <span>{mainText[1]}</span>
                            </>
                        ) : (
                            mainText
                        )}
                    </div>
                </div>
            )}
        </div>
      );
  };

  const renderRowLabels = (side: 'left' | 'right') => (
      <div className={`flex flex-col gap-0 ${side === 'left' ? 'mr-1 items-end' : 'ml-1 items-start'}`} style={{ paddingTop: '4px' }}>
          {Array.from({ length: rows }).map((_, rIndex) => (
               <div 
                key={rIndex} 
                className="flex items-center justify-center text-gray-400 font-medium whitespace-nowrap select-none print:text-black print:font-bold print:text-sm" 
                style={{ height: cellSize, fontSize: `${sideLabelSize}px` }}
               >
                   {rowLabelMap.get(rIndex) || ''}
               </div>
          ))}
      </div>
  );

  return (
    <div className="inline-block p-4 bg-white rounded-xl shadow-lg overflow-auto max-w-full border border-gray-100 print:shadow-none print:border-none print:overflow-visible">
      
      <div className="flex">
          {/* Left Labels */}
          {renderRowLabels('left')}

          {/* Grid */}
          <div 
            className="grid gap-0 bg-slate-50 border border-gray-100 p-1 mx-auto print:bg-white print:border-none"
            style={{ 
              gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
              gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
            }}
          >
            {grid.map((row, rIndex) => (
              <React.Fragment key={rIndex}>
                {row.map((seat, cIndex) => renderSeat(seat, rIndex, cIndex))}
              </React.Fragment>
            ))}
          </div>
          
          {/* Right Labels */}
          {renderRowLabels('right')}
      </div>
      
      {/* Legend Updated */}
      <div className="mt-6 flex gap-4 text-xs text-gray-500 justify-center flex-wrap no-print">
         <div className="flex items-center gap-1"><div className="w-4 h-4 bg-white border border-gray-300 rounded"></div> 空座</div>
         <div className="flex items-center gap-1"><div className="w-4 h-4 bg-sky-200 border border-sky-300 rounded"></div> 座位</div>
         <div className="flex items-center gap-1"><div className="w-4 h-4 bg-amber-100 border border-amber-400 rounded"></div> 主席台</div>
         <div className="flex items-center gap-1"><div className="w-4 h-4 bg-gray-300 rounded"></div> 过道</div>
         <div className="flex items-center gap-1"><div className="w-4 h-4 bg-red-50 border border-red-200 rounded"></div> 不可用</div>
      </div>
    </div>
  );
};
